/**
 * The symbol table + its definition lookup (Blueprint v2 §3.5). Answers "is this
 * symbol already defined?" from an AST index, never regex. Indexed by name AND by
 * file, so a single file's symbols can be replaced incrementally on a write.
 */
import { redactSecrets } from './redact.js';
import type { ScannedFile } from './scan.js';
import type { SymbolDef, SymbolExtractor, SymbolKind } from './symbols.js';
import { TypeScriptSymbolExtractor } from './ts-symbols.js';

/** Registered extractors, tried in order (first whose `supports()` matches wins). */
export const DEFAULT_EXTRACTORS: readonly SymbolExtractor[] = [new TypeScriptSymbolExtractor()];

/** Extract symbols from one file with the registered extractors (no parser → []). */
export function extractSymbols(
  path: string,
  content: string,
  extractors: readonly SymbolExtractor[] = DEFAULT_EXTRACTORS,
): SymbolDef[] {
  const ex = extractors.find((e) => e.supports(path));
  if (!ex) return [];
  try {
    // Redact secrets that could ride along in a default-parameter value or typed
    // initializer captured by the signature (the extractor reads raw source).
    return ex.extract(path, content).map((d) =>
      d.signature ? { ...d, signature: redactSecrets(d.signature).text } : d,
    );
  } catch {
    return []; // a parse error must never abort indexing
  }
}

/** Extract symbols across many files. */
export function extractSymbolsFromFiles(
  files: readonly ScannedFile[],
  extractors: readonly SymbolExtractor[] = DEFAULT_EXTRACTORS,
): SymbolDef[] {
  const out: SymbolDef[] = [];
  for (const f of files) out.push(...extractSymbols(f.path, f.content, extractors));
  return out;
}

export interface SymbolStats {
  readonly files: number;
  readonly symbols: number;
}

export class SymbolIndex {
  private readonly byName = new Map<string, SymbolDef[]>();
  private readonly byFile = new Map<string, SymbolDef[]>();

  /** Replace all symbols for `file` (incremental update on a write). */
  setFile(file: string, defs: readonly SymbolDef[]): void {
    this.removeFile(file);
    if (defs.length === 0) return;
    const copy = [...defs];
    this.byFile.set(file, copy);
    for (const d of copy) {
      const arr = this.byName.get(d.name);
      if (arr) arr.push(d);
      else this.byName.set(d.name, [d]);
    }
  }

  /** Drop all symbols for `file`. */
  removeFile(file: string): void {
    const existing = this.byFile.get(file);
    if (!existing) return;
    this.byFile.delete(file);
    for (const d of existing) {
      const arr = this.byName.get(d.name);
      if (!arr) continue;
      const left = arr.filter((x) => x.file !== file);
      if (left.length > 0) this.byName.set(d.name, left);
      else this.byName.delete(d.name);
    }
  }

  /** Is a symbol with this name (optionally of this kind) already defined? */
  isDefined(name: string, kind?: SymbolKind): boolean {
    const defs = this.byName.get(name);
    if (!defs || defs.length === 0) return false;
    return kind ? defs.some((d) => d.kind === kind) : true;
  }

  /** Every exact-match definition of `name`, across files. */
  lookup(name: string): SymbolDef[] {
    return this.byName.get(name)?.slice() ?? [];
  }

  /** Fuzzy substring match (case-insensitive), capped at `limit`. */
  search(query: string, limit = 25): SymbolDef[] {
    const q = query.toLowerCase();
    const hits: SymbolDef[] = [];
    for (const defs of this.byFile.values()) {
      for (const d of defs) {
        if (d.name.toLowerCase().includes(q)) {
          hits.push(d);
          if (hits.length >= limit) return hits;
        }
      }
    }
    return hits;
  }

  all(): SymbolDef[] {
    const out: SymbolDef[] = [];
    for (const defs of this.byFile.values()) out.push(...defs);
    return out;
  }

  stats(): SymbolStats {
    return { files: this.byFile.size, symbols: this.all().length };
  }

  /** Flat serialization (persisted alongside the vector index). */
  toJSON(): SymbolDef[] {
    return this.all();
  }

  /** Rebuild from a flat serialization. */
  static fromJSON(defs: readonly SymbolDef[]): SymbolIndex {
    const idx = new SymbolIndex();
    const byFile = new Map<string, SymbolDef[]>();
    for (const d of defs) {
      const arr = byFile.get(d.file);
      if (arr) arr.push(d);
      else byFile.set(d.file, [d]);
    }
    for (const [file, fileDefs] of byFile) idx.setFile(file, fileDefs);
    return idx;
  }
}
